import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { Link } from "react-router-dom";

export default function TicketsReservations() {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [paidOrders, setPaidOrders] = useState([]);
  const [acceptedOrders, setAcceptedOrders] = useState([]);
  const [doneOrders, setDoneOrders] = useState([]);
  const [soldOrders, setSoldOrders] = useState([]);
  const [acceptedSeatMap, setAcceptedSeatMap] = useState({});
  const [loadingAcceptedSeats, setLoadingAcceptedSeats] = useState(false);
  const [error, setError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const toursMeta = [
    { id: "2026-tura-1", label: "Tura 1", hour: "13:00", number: 1 },
    { id: "2026-tura-2", label: "Tura 2", hour: "15:00", number: 2 },
    { id: "2026-tura-3", label: "Tura 3", hour: "17:00", number: 3 },
    { id: "2026-tura-4", label: "Tura 4", hour: "19:00", number: 4 },
  ];

  const getTourMeta = (tourNumber) => {
    return toursMeta.find((t) => t.number === Number(tourNumber)) || null;
  };

  const getTourDocId = (tourNumber) => {
    return `2026-tura-${tourNumber}`;
  };

  const getSeatKey = (tour, row, num) => {
    return `${getTourDocId(tour)}_${String(row || "").toLowerCase()}_${Number(num)}`;
  };

  useEffect(() => {
    if (!auth?.currentUser?.uid) return;

    const uid = auth.currentUser.uid;

    const qPending = query(
      collection(db, "gala-purchases2026"),
      where("customer", "==", uid),
      where("status", "==", "pending")
    );

    const qAccepted = query(
      collection(db, "gala-purchases2026"),
      where("customer", "==", uid),
      where("status", "==", "accepted")
    );

    const qDone = query(
      collection(db, "gala-purchases2026"),
      where("customer", "==", uid),
      where("status", "==", "done")
    );

    const qSold = query(
      collection(db, "gala-purchases2026"),
      where("customer", "==", uid),
      where("status", "==", "sold")
    );

    const unsubPending = onSnapshot(
      qPending,
      (snap) => {
        setPendingOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => {
        setError("Nie udało się pobrać oczekujących zamówień.");
      }
    );

    const unsubAccepted = onSnapshot(
      qAccepted,
      (snap) => {
        setAcceptedOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => {
        setError("Nie udało się pobrać opłaconych biletów.");
      }
    );

    const unsubDone = onSnapshot(
      qDone,
      (snap) => {
        setDoneOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => {
        setError("Nie udało się pobrać opłaconych biletów.");
      }
    );

    const unsubSold = onSnapshot(
      qSold,
      (snap) => {
        setSoldOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => {
        setError("Nie udało się pobrać opłaconych biletów.");
      }
    );

    return () => {
      unsubPending();
      unsubAccepted();
      unsubDone();
      unsubSold();
    };
  }, []);

  useEffect(() => {
    setPaidOrders([...acceptedOrders, ...doneOrders, ...soldOrders]);
  }, [acceptedOrders, doneOrders, soldOrders]);

  useEffect(() => {
    const loadAcceptedSeats = async () => {
      if (!doneOrders.length) {
        setAcceptedSeatMap({});
        return;
      }

      try {
        setLoadingAcceptedSeats(true);

        const tourIds = [...new Set(
          doneOrders.flatMap((order) =>
            (order?.seats || []).map((seat) => getTourDocId(seat?.tour))
          ).filter(Boolean)
        )];

        const docs = await Promise.all(
          tourIds.map(async (tourId) => {
            const snap = await getDoc(doc(db, "gala", tourId));
            return { tourId, data: snap.exists() ? snap.data() || {} : {} };
          })
        );

        const nextMap = {};

        docs.forEach(({ tourId, data }) => {
          const seatsObj = data?.seats || {};
          Object.entries(seatsObj).forEach(([rowKey, rowSeats]) => {
            Object.values(rowSeats || {}).forEach((seatData) => {
              const seatNum = Number(seatData?.seat);
              const key = `${tourId}_${String(rowKey || "").toLowerCase()}_${seatNum}`;
              nextMap[key] = seatData?.status || "available";
            });
          });
        });

        setAcceptedSeatMap(nextMap);
      } catch (err) {
        console.error(err);
        setError("Nie udało się zweryfikować statusów miejsc.");
      } finally {
        setLoadingAcceptedSeats(false);
      }
    };

    loadAcceptedSeats();
  }, [doneOrders]);

  const waitingTickets = useMemo(() => {
    return pendingOrders.flatMap((order) =>
      (order?.seats || []).map((seat, index) => ({
        ticketId: `${order.id}_${seat.id || index}`,
        orderId: order.id,
        date: order.createdAt,
        row: seat.row,
        num: seat.num,
        price: seat.finalPrice,
        isDiscount: seat.isDiscount,
        tour: seat?.tour,
        orderStatus: order?.status || "pending",
      }))
    );
  }, [pendingOrders]);

  const ticketsData = useMemo(() => {
    const validTickets = [];
    const invalidTickets = [];

    paidOrders.forEach((order) => {
      const orderStatus = order?.status || "";

      (order?.seats || []).forEach((seat, index) => {
        const seatKey = getSeatKey(seat?.tour, seat?.row, seat?.num);
        const liveSeatStatus = acceptedSeatMap?.[seatKey];

        const baseTicket = {
          ticketId: `${order.id}_${seat.id || index}`,
          orderId: order.id,
          date: order.createdAt,
          row: seat.row,
          num: seat.num,
          price: seat.finalPrice,
          isDiscount: seat.isDiscount,
          tour: seat?.tour,
          orderStatus,
          liveSeatStatus,
        };

        if (orderStatus === "done" && liveSeatStatus === "accepted") {
          validTickets.push(baseTicket);
        } else {
          let reason = "";

          if (orderStatus !== "done") {
            reason = `Zamówienie ma status "${orderStatus}", a nie "done".`;
          } else if (liveSeatStatus !== "accepted") {
            reason = `Miejsce nie ma statusu "accepted" (aktualnie: "${liveSeatStatus || "brak"}").`;
          }

          invalidTickets.push({
            ...baseTicket,
            reason,
          });
        }
      });
    });

    return { validTickets, invalidTickets };
  }, [paidOrders, acceptedSeatMap]);

  const tickets = ticketsData.validTickets;
  const invalidTickets = ticketsData.invalidTickets;

  const pendingOrdersSummary = useMemo(() => {
    return pendingOrders.map((order) => {
      const seats = (order?.seats || []).map((seat) => {
        const tour = getTourMeta(seat?.tour);
        return {
          ...seat,
          ticketLabel: `Tura ${seat?.tour}${tour?.hour ? ` | ${tour.hour}` : ""} | Rząd: ${String(seat?.row || "").toUpperCase()} | Miejsce: ${seat?.num}`,
        };
      });

      return {
        ...order,
        seats,
      };
    });
  }, [pendingOrders]);

  const doneOrdersSummary = useMemo(() => {
  return doneOrders.map((order) => {
    const seats = (order?.seats || []).map((seat) => {
      const tour = getTourMeta(seat?.tour);
      return {
        ...seat,
        ticketLabel: `Tura ${seat?.tour}${tour?.hour ? ` | ${tour.hour}` : ""} | Rząd: ${String(seat?.row || "").toUpperCase()} | Miejsce: ${seat?.num}`,
      };
    });

    return {
      ...order,
      seats,
    };
  });
}, [doneOrders]);

  const ticketStyles = StyleSheet.create({
    body: {
      paddingTop: 25,
      paddingBottom: 65,
      paddingHorizontal: 35,
      width: "50%",
    },
    imageBack: {
      position: "absolute",
      left: "0px",
      right: "0px",
      width: "100vw",
      height: "100vh",
      marginHorizontal: "auto",
    },
  });

  const TicketDocumentSeat = ({ ticket }) => (
    <Document>
      <Page size="A4" style={ticketStyles.body}>
        <Image
          style={ticketStyles.imageBack}
          src={process.env.PUBLIC_URL + "/ticket.png"}
          fixed
        />
        <Text style={{ top: "150px", left: "33px", fontSize: "11px", fontWeight: "bold" }}>
          {ticket?.tour}
          {ticket?.row?.toUpperCase()}
          {ticket?.num}
        </Text>
        <Text style={{ top: "142px", left: "118px", fontSize: "7px", fontWeight: "bold" }}>
          Bilet {ticket?.isDiscount ? "ulgowy" : "normalny"}
        </Text>
        <Text style={{ top: "135px", left: "241px", fontSize: "7px", fontWeight: "bold" }}>
          Tura {ticket?.tour}
        </Text>
        <Text style={{ top: "127px", left: "351px", fontSize: "7px", fontWeight: "bold" }}>
          {ticket?.row?.toUpperCase()}
        </Text>
        <Text style={{ top: "118.5px", left: "458px", fontSize: "7px", fontWeight: "bold" }}>
          {ticket?.num}
        </Text>
      </Page>
    </Document>
  );

  const handleCancelOrder = async () => {
    if (!confirmCancel?.id) return;

    try {
      setCancelLoading(true);
      setError("");

      const order = confirmCancel;
      const seats = Array.isArray(order?.seats) ? order.seats : [];

      for (const seat of seats) {
        const tourDocId = getTourDocId(seat?.tour);
        const row = seat?.row;
        const num = seat?.num;

        if (!tourDocId || !row || num === undefined || num === null) continue;

        await setDoc(
          doc(db, "gala", tourDocId),
          {
            seats: {
              [row]: {
                [num]: {
                  seat: Number(num),
                  price: seat?.price || 0,
                  reduced: false,
                  reserved: false,
                  status: "available",
                  ownerUid: "",
                  purchaseId: "",
                  finalPrice: "",
                },
              },
            },
          },
          { merge: true }
        );
      }

      await setDoc(
        doc(db, "gala-purchases2026", order.id),
        {
          status: "canceled",
          canceledAt: serverTimestamp(),
        },
        { merge: true }
      );

      setConfirmCancel(null);
    } catch (err) {
      console.error(err);
      setError("Nie udało się anulować zamówienia.");
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "rgb(240,240,240)",
        width: "100%",
        minHeight: "100vh",
        gap: "30px",
        position: "relative",
        overflow: "hidden",
        paddingBottom: "40px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          position: "fixed",
          zIndex: "40",
          top: error ? "45px" : "-300px",
          transform: "translate(-50%, -50%)",
          left: "50%",
          backgroundColor: "rgb(250,250,250)",
          width: "320px",
          height: "75px",
          transition: "0.8s",
          borderRadius: "15px",
          border: "3px solid rgb(215,0,64)",
          gap: "20px",
        }}
      >
        <img src={process.env.PUBLIC_URL + "/error.png"} style={{ width: "30px" }} />
        <h3
          style={{
            fontSize: "13px",
            fontWeight: "bold",
            color: "rgb(215,0,64)",
            textAlign: "center",
            width: "75%",
          }}
        >
          {error}
        </h3>
      </div>

      <h2 style={{ fontSize: "20px", fontWeight: "bold", marginTop: "50px" }}>
        Oczekujące bilety
      </h2>

      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "row",
          gap: "15px",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {waitingTickets.length ? (
          waitingTickets.map((t) => {
            const tour = getTourMeta(t?.tour);

            return (
              <div
                key={t.ticketId}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "130px",
                  height: "145px",
                  padding: "20px",
                  boxShadow:
                    "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                  margin: "3px",
                  borderRadius: "3px",
                  backgroundColor: "rgb(255,255,255,0.75)",
                }}
              >
                <img src="invit_blue.png" style={{ width: "37.5px", marginBottom: "10px" }} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "2px",
                    marginBottom: "10px",
                  }}
                >
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      textAlign: "center",
                    }}
                  >
                    {t?.isDiscount ? "Bilet ulgowy " + t?.price + " zł" : "Bilet normalny " + t?.price + " zł"}
                  </h3>
                  <h3
                    style={{
                      color: "#fcba03",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      textAlign: "center",
                    }}
                  >
                    Status: Oczekujący
                  </h3>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "2px",
                    width: "100%",
                  }}
                >
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Rząd: {t?.row?.toUpperCase()}
                  </h3>
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Miejsce: {t?.num}
                  </h3>
                </div>

                <h3
                  style={{
                    color: "rgb(35,35,35)",
                    fontSize: "11px",
                    fontWeight: "bold",
                    letterSpacing: "0.1px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Tura: {t?.tour} {tour?.hour ? `| ${tour.hour}` : ""}
                </h3>
              </div>
            );
          })
        ) : (
          <p style={{ color: "#666" }}>Brak oczekujących biletów</p>
        )}
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "0px" }}>
          Oczekujące zamówienia
        </h2>

        {pendingOrdersSummary.length ? (
          pendingOrdersSummary.map((order) => (
            <div
              key={order.id}
              style={{
                width: "92%",
                maxWidth: "760px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: "10px",
                padding: "18px 20px",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                borderRadius: "5px",
                backgroundColor: "rgb(255,255,255,0.75)",
              }}
            >
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <h3
                  style={{
                    color: "rgb(35,35,35)",
                    fontSize: "14px",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  Zamówienie: {order.id}
                </h3>

                <button
                  onClick={() => setConfirmCancel(order)}
                  style={{
                    fontSize: "11px",
                    color: "white",
                    borderRadius: "5px",
                    backgroundColor: "#DC143C",
                    border: "2px solid #DC143C",
                    fontWeight: "bold",
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Anuluj zamówienie
                </button>
              </div>

              <h3
                style={{
                  color: "#fcba03",
                  fontSize: "12px",
                  fontWeight: "bold",
                  margin: 0,
                }}
              >
                Status: oczekujące
              </h3>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  width: "100%",
                }}
              >
                {order?.seats?.map((seat, index) => (
                  <h3
                    key={`${order.id}_${index}`}
                    style={{
                      color: "rgb(60,60,60)",
                      fontSize: "12px",
                      fontWeight: "bold",
                      margin: 0,
                    }}
                  >
                    {seat.ticketLabel}
                  </h3>
                ))}
              </div>

              <h3
                style={{
                  color: "#0045C7",
                  fontSize: "13px",
                  fontWeight: "bold",
                  margin: 0,
                }}
              >
                Suma do wpłaty: {order?.total || 0} zł
              </h3>
            </div>
          ))
        ) : (
          <p style={{ color: "#666" }}>Brak oczekujących zamówień</p>
        )}
      </div>

      <h2 style={{ fontSize: "20px", fontWeight: "bold" }}>Twoje opłacone bilety</h2>

      {loadingAcceptedSeats && (
        <p style={{ color: "#666", marginTop: "-15px" }}>
          Weryfikuję miejsca...
        </p>
      )}

      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "row",
          gap: "15px",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {tickets.length ? (
          tickets.map((t) => {
            const tour = getTourMeta(t?.tour);

            return (
              <div
                key={t.ticketId}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "130px",
                  height: "160px",
                  padding: "20px",
                  boxShadow:
                    "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                  margin: "3px",
                  borderRadius: "3px",
                  backgroundColor: "rgb(255,255,255,0.75)",
                }}
              >
                <img src="invit_blue.png" style={{ width: "37.5px", marginBottom: "10px" }} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "2px",
                    marginBottom: "10px",
                  }}
                >
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      textAlign: "center",
                    }}
                  >
                    {t?.isDiscount ? "Bilet ulgowy " + t?.price + " zł" : "Bilet normalny " + t?.price + " zł"}
                  </h3>
                  <h3
                    style={{
                      color: "green",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                      textAlign: "center",
                    }}
                  >
                    Status: Opłacony
                  </h3>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "2px",
                    width: "100%",
                  }}
                >
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Rząd: {t?.row?.toUpperCase()}
                  </h3>
                  <h3
                    style={{
                      color: "rgb(35,35,35)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      letterSpacing: "0.1px",
                    }}
                  >
                    Miejsce: {t?.num}
                  </h3>
                </div>

                <h3
                  style={{
                    color: "rgb(35,35,35)",
                    fontSize: "11px",
                    fontWeight: "bold",
                    letterSpacing: "0.1px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Tura: {t?.tour} {tour?.hour ? `| ${tour.hour}` : ""}
                </h3>

                <PDFDownloadLink
                  document={<TicketDocumentSeat ticket={t} />}
                  fileName={`bilet-${t?.tour}-${t?.row}-${t?.num}.pdf`}
                  style={{ textDecoration: "none" }}
                >
                  <button
                    style={{
                      background: "#0045C7",
                      border: "none",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "3px",
                      cursor: "pointer",
                      marginTop: "10px",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Pobierz bilet
                  </button>
                </PDFDownloadLink>
              </div>
            );
          })
        ) : (
          <p style={{ color: "#666" }}>Brak zakupionych biletów</p>
        )}
      </div>

      <div
  style={{
    width: "100%",
    maxWidth: "1200px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    justifyContent: "center",
    alignItems: "center",
  }}
>
  <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "0px" }}>
    Opłacone zamówienia
  </h2>

    {doneOrdersSummary.length ? (
        doneOrdersSummary.map((order) => (
        <div
            key={order.id}
            style={{
            width: "92%",
            maxWidth: "760px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "10px",
            padding: "18px 20px",
            boxShadow:
                "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
            borderRadius: "5px",
            backgroundColor: "rgb(255,255,255,0.75)",
            }}
        >
            <h3
            style={{
                color: "rgb(35,35,35)",
                fontSize: "14px",
                fontWeight: "bold",
                margin: 0,
            }}
            >
            Zamówienie: {order.id}
            </h3>

            <h3
            style={{
                color: "#12B552",
                fontSize: "12px",
                fontWeight: "bold",
                margin: 0,
            }}
            >
            Status: opłacone
            </h3>

            <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                width: "100%",
            }}
            >
            {order?.seats?.map((seat, index) => (
                <h3
                key={`${order.id}_${index}`}
                style={{
                    color: "rgb(60,60,60)",
                    fontSize: "12px",
                    fontWeight: "bold",
                    margin: 0,
                }}
                >
                {seat.ticketLabel}
                </h3>
            ))}
            </div>

            <h3
            style={{
                color: "#0045C7",
                fontSize: "13px",
                fontWeight: "bold",
                margin: 0,
            }}
            >
            Suma opłacona: {order?.total || 0} zł
            </h3>
        </div>
        ))
    ) : (
        <p style={{ color: "#666" }}>Brak opłaconych zamówień</p>
    )}
    </div>

      {invalidTickets.length > 0 && (
        <div
          style={{
            width: "100%",
            maxWidth: "1200px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "0px" }}>
            Błędy w biletach
          </h2>

          {invalidTickets.map((t) => {
            const tour = getTourMeta(t?.tour);

            return (
              <div
                key={`invalid_${t.ticketId}`}
                style={{
                  width: "92%",
                  maxWidth: "760px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "18px 20px",
                  boxShadow:
                    "rgba(0, 0, 0, 0.05) 0px 0px 0px 1.5px, rgb(209, 213, 219) 0px 0px 0px 1px",
                  borderRadius: "5px",
                  backgroundColor: "rgba(220,20,60,0.06)",
                  border: "1px solid rgba(220,20,60,0.18)",
                }}
              >
                <h3
                  style={{
                    color: "#DC143C",
                    fontSize: "14px",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  Bilet nie został zaakceptowany
                </h3>

                <h3
                  style={{
                    color: "rgb(35,35,35)",
                    fontSize: "13px",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  Tura: {t?.tour} {tour?.hour ? `| ${tour.hour}` : ""} | Rząd: {String(t?.row || "").toUpperCase()} | Miejsce: {t?.num}
                </h3>

                <h3
                  style={{
                    color: "rgb(80,80,80)",
                    fontSize: "12px",
                    fontWeight: "normal",
                    margin: 0,
                  }}
                >
                  ID zamówienia: {t?.orderId}
                </h3>

                <h3
                  style={{
                    color: "#DC143C",
                    fontSize: "12px",
                    fontWeight: "bold",
                    margin: 0,
                  }}
                >
                  {t?.reason}
                </h3>
              </div>
            );
          })}
        </div>
      )}

      {confirmCancel && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 60,
          }}
        >
          <div
            style={{
              width: "92%",
              maxWidth: "520px",
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                color: "rgb(30,30,30)",
                margin: 0,
              }}
            >
              Potwierdzenie anulowania
            </h3>

            <h3
              style={{
                fontSize: "14px",
                color: "rgb(30,30,30)",
                fontWeight: "normal",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Czy na pewno chcesz anulować to zamówienie?
            </h3>

            <h3
              style={{
                fontSize: "13px",
                color: "#0045C7",
                margin: 0,
              }}
            >
              Zamówienie: {confirmCancel.id}
            </h3>

            <h3
              style={{
                fontSize: "13px",
                color: "rgb(30,30,30,0.75)",
                fontWeight: "normal",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Spowoduje to przywrócenie miejsc do rezerwacji. Twoje bilety, nawet jeśli przelew został już wysłany, nie będą uwzględnione.
            </h3>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "10px",
                marginTop: "6px",
              }}
            >
              <button
                onClick={() => setConfirmCancel(null)}
                style={{
                  fontSize: "12px",
                  color: "rgb(30,30,30)",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid rgb(30,30,30,0.2)",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Anuluj
              </button>

              <button
                onClick={handleCancelOrder}
                disabled={cancelLoading}
                style={{
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "5px",
                  backgroundColor: "#DC143C",
                  border: "2px solid #DC143C",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: cancelLoading ? "default" : "pointer",
                  opacity: cancelLoading ? 0.75 : 1,
                }}
              >
                {cancelLoading ? "Anulowanie..." : "Potwierdź anulowanie"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}